import React, { useMemo, useState } from 'react';
import { Button, message } from 'antd';
import {
  ApiOutlined,
  CheckOutlined,
  CopyOutlined,
  SearchOutlined,
  UnorderedListOutlined,
  FileSearchOutlined,
} from '@ant-design/icons';
import PublicActionPage from '../components/PublicActionPage';
import { getMcpUrl, isMcpEnabled } from '../../../core/config/runtimeConfig';

const MCP_TOOLS = [
  {
    name: 'search_jobs',
    summary: 'Search published jobs by query, category, subcategory, and page.',
  },
  {
    name: 'get_job',
    summary: 'Fetch public details for one published job UUID.',
  },
  {
    name: 'list_job_categories',
    summary: 'List valid categories and subcategories for filters.',
  },
] as const;

const Developers: React.FC = () => {
  const mcpEnabled = isMcpEnabled();
  const mcpUrl = useMemo(() => getMcpUrl(), []);
  const [copied, setCopied] = useState(false);

  const codexCommand = mcpUrl
    ? `codex mcp add job_posting --url ${mcpUrl}`
    : 'codex mcp add job_posting --url https://your-job-posting-host.example/mcp';

  const copyText = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      message.success(`${label} copied`);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      message.error(`Could not copy ${label.toLowerCase()}`);
    }
  };

  if (!mcpEnabled) {
    return (
      <main className="public-page-shell w-full">
        <div className="public-content-rail public-action-page">
          <section className="public-panel">
            <div className="public-panel-body">
              <h1 className="jo-heading m-0 text-2xl font-bold">
                MCP server unavailable
              </h1>
              <p className="jo-body-muted mb-0 mt-3 text-base leading-7">
                The Model Context Protocol endpoint is disabled for this
                deployment.
              </p>
            </div>
          </section>
        </div>
      </main>
    );
  }

  return (
    <PublicActionPage
      eyebrow="For AI assistants"
      title="Connect with MCP"
      description="Use Model Context Protocol (Streamable HTTP) to search published roles, read job details, and list categories — read-only, no login required. Candidate and recruiter data are not available through this server."
      icon={<ApiOutlined />}
      steps={[
        {
          title: 'Search published jobs',
          detail: 'Filter by keyword, category, and subcategory.',
        },
        {
          title: 'Fetch one job by UUID',
          detail: 'Read full public details for a single role.',
        },
        {
          title: 'List categories',
          detail: 'Use valid categories to power accurate filters.',
        },
      ]}
      asideNote="Read-only public data. Candidate and recruiter records are not exposed."
      formTitle="MCP connection"
      formDescription="Point any Streamable HTTP MCP client at the URL below. Cursor, Codex, Claude Desktop, and other hosts that speak MCP can plug in without a custom REST client."
    >
      <div className="ats-stack space-y-5">
        <div>
          <p className="public-eyebrow mb-2">Endpoint</p>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-stretch">
            <code
              className="jo-body-muted flex-1 break-all rounded-lg border border-[var(--jo-border)] bg-[var(--jo-surface-muted)] px-3 py-3 text-sm"
              data-testid="mcp-url"
            >
              {mcpUrl ||
                'Set REACT_APP_MCP_URL or REACT_APP_API_URL to show the public endpoint'}
            </code>
            <Button
              type="primary"
              className="public-button-primary sm:self-stretch"
              icon={copied ? <CheckOutlined /> : <CopyOutlined />}
              disabled={!mcpUrl}
              onClick={() => {
                if (mcpUrl) {
                  void copyText(mcpUrl, 'MCP URL');
                }
              }}
            >
              {copied ? 'Copied' : 'Copy MCP URL'}
            </Button>
          </div>
          <p className="jo-body-subtle mb-0 mt-2 text-sm">
            Transport: Streamable HTTP · Auth: none · Scope: published jobs only
          </p>
        </div>

        <div>
          <p className="public-eyebrow mb-2">Tools</p>
          <ul className="m-0 list-none space-y-3 p-0">
            {MCP_TOOLS.map((tool) => (
              <li
                key={tool.name}
                className="rounded-lg border border-[var(--jo-border)] px-3 py-3"
              >
                <div className="flex items-start gap-2">
                  {tool.name === 'search_jobs' && (
                    <SearchOutlined className="jo-accent-text mt-0.5" />
                  )}
                  {tool.name === 'get_job' && (
                    <FileSearchOutlined className="jo-accent-text mt-0.5" />
                  )}
                  {tool.name === 'list_job_categories' && (
                    <UnorderedListOutlined className="jo-accent-text mt-0.5" />
                  )}
                  <div>
                    <code className="text-sm font-semibold text-[var(--jo-text)]">
                      {tool.name}
                    </code>
                    <p className="jo-body-muted mb-0 mt-1 text-sm leading-6">
                      {tool.summary}
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="public-eyebrow mb-2">Codex example</p>
          <pre className="jo-body-muted mb-3 overflow-x-auto rounded-lg border border-[var(--jo-border)] bg-[var(--jo-surface-muted)] px-3 py-3 text-sm leading-6">
            {codexCommand}
          </pre>
          <Button
            icon={<CopyOutlined />}
            onClick={() => {
              void copyText(codexCommand, 'Command');
            }}
          >
            Copy setup command
          </Button>
        </div>
      </div>
    </PublicActionPage>
  );
};

export default Developers;
